import { useState, useEffect, useCallback } from 'react';
import { calendarAPI } from '../../lib/api';
import { AdminLayout } from '../../components/AdminLayout';
import { useProperty } from '../../contexts/PropertyContext';
import { getCurrencySymbol } from '../../lib/currencies';
import { Loader2, ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { toast } from 'sonner';

const DEMAND_COLORS = {
  low: 'bg-[#e8f5e9] text-gray-700',
  normal: 'bg-[#c8e6c9] text-gray-800',
  good: 'bg-[#b3d4fc] text-gray-800',
  high: 'bg-[#90caf9] text-gray-900',
  unavailable: 'bg-gray-200 text-gray-400',
};

const DEMAND_LEGEND = [
  { key: 'low', label: 'Low', color: 'bg-[#e8f5e9]' },
  { key: 'normal', label: 'Normal', color: 'bg-[#c8e6c9]' },
  { key: 'good', label: 'Good', color: 'bg-[#b3d4fc]' },
  { key: 'high', label: 'High', color: 'bg-[#90caf9]' },
  { key: 'unavailable', label: 'Unavailable', color: 'bg-gray-200' },
];

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const VIEW_OPTIONS = [
  { value: 9, label: 'Yearly' },
  { value: 3, label: 'Quarterly' },
  { value: 1, label: 'Monthly' },
];

function MonthGrid({ month }) {
  const firstDay = new Date(month.year, month.month - 1, 1).getDay();
  const blanks = Array(firstDay).fill(null);
  const cells = [...blanks, ...month.days];

  return (
    <div className="border border-gray-200 rounded bg-white" data-testid={`month-${month.year}-${month.month}`}>
      {/* Month Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">
          {month.month_name} {month.year}
        </h3>
        <span className="text-xs text-gray-500 font-medium">
          {month.availability_pct}% Availability
        </span>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-100">
        {DAYS_OF_WEEK.map((d) => (
          <div key={d} className="text-[10px] font-semibold text-gray-400 text-center py-1 uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          if (!day) {
            return <div key={`blank-${i}`} className="h-[52px] border-r border-b border-gray-50" />;
          }
          const demandClass = DEMAND_COLORS[day.demand] || DEMAND_COLORS.low;
          return (
            <div
              key={day.date}
              className={`h-[52px] border-r border-b border-gray-100 px-1 py-0.5 flex flex-col justify-between ${demandClass} transition-colors hover:opacity-80`}
              title={`${day.date}\nBookings: ${day.bookings}\nRate: ${day.rate}\nOccupancy: ${day.occupancy}%`}
              data-testid={`day-cell-${day.date}`}
            >
              <div className="flex items-baseline justify-between">
                {day.bookings > 0 && (
                  <span className="text-[9px] font-bold text-red-600 leading-none">
                    {day.bookings}C
                  </span>
                )}
                <span className={`text-[11px] font-medium leading-none ${day.bookings > 0 ? '' : 'ml-auto'}`}>
                  {day.day}
                </span>
              </div>
              <div className="text-[10px] font-medium text-gray-600 leading-none text-center">
                {day.rate > 0 ? Math.round(day.rate) : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CalendarContent() {
  const { selectedProperty } = useProperty();
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMonths, setViewMonths] = useState(9);
  const [startMonth, setStartMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await calendarAPI.get(startMonth, viewMonths, selectedProperty || undefined);
      setCalendarData(res.data);
    } catch {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [startMonth, viewMonths, selectedProperty]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const navigateMonths = (direction) => {
    const [y, m] = startMonth.split('-').map(Number);
    let newMonth = m + (direction * viewMonths);
    let newYear = y;
    while (newMonth > 12) { newMonth -= 12; newYear++; }
    while (newMonth < 1) { newMonth += 12; newYear--; }
    setStartMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setStartMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  };

  // Determine grid columns based on view
  const gridCols = viewMonths === 1 ? 'grid-cols-1 max-w-lg mx-auto' :
                   viewMonths === 3 ? 'grid-cols-1 md:grid-cols-3' :
                   'grid-cols-1 md:grid-cols-2 xl:grid-cols-3';

  return (
    <div className="page-wrapper" data-testid="calendar-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Availability</h1>
          <p className="text-sm text-muted-foreground mt-1">Yearly availability and demand overview.</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={goToCurrentMonth}
            className="px-4 py-2 rounded-full border bg-white border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
            data-testid="calendar-today-btn"
          >
            CURRENT
          </button>
          <div className="flex items-center gap-0">
            <button
              onClick={() => navigateMonths(-1)}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="calendar-prev-btn"
            >
              <ChevronLeft className="h-5 w-5 text-gray-500" />
            </button>
            <button
              onClick={() => navigateMonths(1)}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="calendar-next-btn"
            >
              <ChevronRight className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          {calendarData?.data?.length > 0 && (
            <h1 className="text-lg font-semibold text-gray-900" data-testid="calendar-range-title">
              {calendarData.data[0].month_name} {calendarData.data[0].year}
              {calendarData.data.length > 1 && (
                <> — {calendarData.data[calendarData.data.length - 1].month_name} {calendarData.data[calendarData.data.length - 1].year}</>
              )}
            </h1>
          )}
        </div>

        <div className="flex items-center gap-2">
          <select
            value={viewMonths}
            onChange={(e) => setViewMonths(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            data-testid="calendar-view-select"
          >
            {VIEW_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button className="p-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors" data-testid="calendar-settings-btn">
            <Settings className="h-4 w-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : calendarData?.data ? (
        <div className={`grid gap-4 ${gridCols}`}>
          {calendarData.data.map((month) => (
            <MonthGrid key={`${month.year}-${month.month}`} month={month} />
          ))}
        </div>
      ) : (
        <div className="text-center py-24 text-gray-400">No calendar data available</div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap pt-2 pb-4 text-xs text-gray-600" data-testid="calendar-legend">
        <span className="font-semibold text-gray-700">Demand:</span>
        {DEMAND_LEGEND.map((item) => (
          <div key={item.key} className="flex items-center gap-1.5">
            <span className={`w-4 h-3 rounded-sm ${item.color} border border-gray-200`} />
            <span>{item.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <span className="text-red-600 font-bold text-[10px]">2C</span>
          <span>= Booking Count</span>
        </div>
      </div>
    </div>
  );
}

export default function AdminCalendar() {
  return (
    <AdminLayout>
      <CalendarContent />
    </AdminLayout>
  );
}
